// src/components/BirthdayPopupDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  AlertTitle,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Pagination,
  Tooltip,
  Avatar,
  alpha,
  useTheme,
  Snackbar,
  SnackbarContent,
  Divider,
  Stack,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Cake as CakeIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Celebration as CelebrationIcon,
  SentimentSatisfiedAlt as SmileIcon,
  ConfirmationNumber as GiftIcon,
  CalendarMonth as CalendarMonthIcon,
  Today as TodayIcon,
} from '@mui/icons-material';
import Confetti from 'react-confetti';

const BirthdayPopupDialog = ({ open, onClose, onSendWishes }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [todayBirthdays, setTodayBirthdays] = useState([]);
  const [monthBirthdays, setMonthBirthdays] = useState([]);
  const [stats, setStats] = useState(null);
  const [departmentBreakdown, setDepartmentBreakdown] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showConfetti, setShowConfetti] = useState(true);
  const [sendingWishes, setSendingWishes] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState(0);

  const currentMonthName = new Date().toLocaleString('default', { month: 'long' });

  useEffect(() => {
    if (open) {
      fetchBirthdayStaff();
      const timer = setTimeout(() => setShowConfetti(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const fetchBirthdayStaff = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const url = `${baseUrl}/get_birthday_staff.php`;
      
      console.log('Fetching birthdays from:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        headers: { 'Accept': 'application/json' },
      });
      
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const result = await response.json();
      console.log('Birthday API Response:', result);
      
      if (result.success) {
        setTodayBirthdays(result.today_birthdays || []);
        setMonthBirthdays(result.month_birthdays || []);
        setStats(result.stats);
        setDepartmentBreakdown(result.department_breakdown || []);
        
        // If there are today's birthdays, show confetti
        if (result.today_birthdays && result.today_birthdays.length > 0) {
          setShowConfetti(true);
        }
      } else {
        setError(result.message || 'Failed to fetch birthday staff data');
      }
    } catch (err) {
      console.error('Error fetching birthday staff:', err);
      setError('Network error occurred. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendWishes = async (staff) => {
    try {
      setSendingWishes(staff.id);
      
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/send_birthday_wishes.php`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          staff_id: staff.id,
          phone: staff.phone,
          email: staff.email,
          name: staff.full_name,
        }),
      });
      
      const result = await response.json();
      
      if (result.success) {
        setSnackbar({
          open: true,
          message: `🎉 Birthday wishes sent to ${staff.full_name} successfully! 🎂`,
          severity: 'success',
        });
        if (onSendWishes) onSendWishes(staff);
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Failed to send wishes',
          severity: 'error',
        });
      }
    } catch (err) {
      console.error('Error sending wishes:', err);
      setSnackbar({
        open: true,
        message: 'Network error. Please try again.',
        severity: 'error',
      });
    } finally {
      setSendingWishes(null);
    }
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  // Get current data based on active tab
  const getCurrentData = () => {
    return activeTab === 0 ? todayBirthdays : monthBirthdays;
  };

  const currentData = getCurrentData();

  const filteredStaff = currentData.filter(staff => {
    const matchesSearch = searchTerm === '' || 
      staff.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      staff.surname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      staff.other_names?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      staff.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      staff.phone?.includes(searchTerm);
    
    const matchesDepartment = departmentFilter === 'all' || staff.department === departmentFilter;
    return matchesSearch && matchesDepartment;
  });

  const totalPages = Math.ceil(filteredStaff.length / rowsPerPage);
  const paginatedStaff = filteredStaff.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  
  // Get unique departments for filter from current data
  const uniqueDepartments = ['all', ...new Set(currentData.map(s => s.department).filter(Boolean))];

  const getAgeColor = (age) => {
    if (age < 30) return '#4caf50';
    if (age < 45) return '#2196f3';
    if (age < 60) return '#ff9800';
    return '#9c27b0';
  };

  const exportToCSV = () => {
    const headers = ['Full Name', 'Department', 'Phone', 'Email', 'Age', 'Birthday', 'Status'];
    const csvData = filteredStaff.map(staff => [
      staff.full_name, 
      staff.department, 
      staff.phone, 
      staff.email,
      staff.age, 
      staff.birthday_display, 
      staff.employment_status
    ]);
    
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `birthday_staff_${activeTab === 0 ? 'today' : 'this_month'}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getGreetingMessage = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning!';
    if (hour < 17) return 'Good Afternoon!';
    return 'Good Evening!';
  };

  // Debug log to see what data we have
  useEffect(() => {
    if (todayBirthdays.length > 0) {
      console.log('Today\'s birthdays:', todayBirthdays);
      todayBirthdays.forEach(staff => {
        console.log(`Birthday: ${staff.full_name} - ${staff.date_of_birth} - Age: ${staff.age}`);
      });
    }
    if (monthBirthdays.length > 0) {
      console.log('This month\'s birthdays:', monthBirthdays.length);
    }
  }, [todayBirthdays, monthBirthdays]);

  return (
    <>
      {showConfetti && todayBirthdays.length > 0 && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={200}
          gravity={0.3}
        />
      )}
      
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            minHeight: '60vh',
            maxHeight: '85vh',
            background: 'linear-gradient(135deg, #fff5e6 0%, #fff 100%)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
          color: 'white',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CakeIcon sx={{ fontSize: 32 }} />
            <Typography variant="h5" fontWeight="bold">
              🎂 Birthday Celebrations! 🎉
            </Typography>
            <Chip 
              label={`${todayBirthdays.length} today • ${monthBirthdays.length} this month`} 
              size="small" 
              sx={{ ml: 1, bgcolor: 'white', color: '#ff9800', fontWeight: 'bold' }} 
            />
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {todayBirthdays.length > 0 && (
            <Alert 
              severity="success" 
              icon={<CelebrationIcon />}
              sx={{ mb: 3, borderRadius: 2, background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)' }}
            >
              <AlertTitle sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                🎊 {getGreetingMessage()} Today is a special day! 🎊
              </AlertTitle>
              <Typography variant="body2">
                We have <strong>{todayBirthdays.length} wonderful staff member(s)</strong> celebrating their birthday TODAY!
                Let's make their day special by sending warm wishes.
              </Typography>
            </Alert>
          )}

          {monthBirthdays.length > 0 && todayBirthdays.length === 0 && (
            <Alert 
              severity="info" 
              icon={<CalendarMonthIcon />}
              sx={{ mb: 3, borderRadius: 2, background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)' }}
            >
              <AlertTitle sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                📅 Upcoming Birthdays This {currentMonthName}
              </AlertTitle>
              <Typography variant="body2">
                <strong>{monthBirthdays.length} staff member(s)</strong> will be celebrating their birthday this month.
                Plan ahead to make their day special!
              </Typography>
            </Alert>
          )}

          {(todayBirthdays.length > 0 || monthBirthdays.length > 0) && stats && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ borderRadius: 2, bgcolor: '#fff3e0' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2" color="textSecondary">Birthday Today</Typography>
                        <Typography variant="h4" color="#ff9800">{todayBirthdays.length}</Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: alpha('#ff9800', 0.1), color: '#ff9800', width: 48, height: 48 }}>
                        <TodayIcon />
                      </Avatar>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ borderRadius: 2, bgcolor: '#e8f5e9' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2" color="textSecondary">This Month</Typography>
                        <Typography variant="h4" color="#4caf50">{monthBirthdays.length}</Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: alpha('#4caf50', 0.1), color: '#4caf50', width: 48, height: 48 }}>
                        <CalendarMonthIcon />
                      </Avatar>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ borderRadius: 2, bgcolor: '#e3f2fd' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2" color="textSecondary">Departments</Typography>
                        <Typography variant="h4">{stats.departments_affected || 0}</Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: alpha('#2196f3', 0.1), color: '#2196f3', width: 48, height: 48 }}>
                        <BusinessIcon />
                      </Avatar>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Tabs for Today vs This Month */}
          {(todayBirthdays.length > 0 || monthBirthdays.length > 0) && (
            <Paper sx={{ mb: 3, borderRadius: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => {
                  setActiveTab(newValue);
                  setPage(1);
                  setSearchTerm('');
                  setDepartmentFilter('all');
                }}
                variant="fullWidth"
                sx={{
                  '& .MuiTab-root': {
                    py: 2,
                    fontSize: '1rem',
                    fontWeight: 'bold',
                  },
                  '& .Mui-selected': {
                    color: '#ff9800',
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#ff9800',
                  },
                }}
              >
                <Tab 
                  icon={<TodayIcon />} 
                  iconPosition="start" 
                  label={`Today (${todayBirthdays.length})`} 
                />
                <Tab 
                  icon={<CalendarMonthIcon />} 
                  iconPosition="start" 
                  label={`This Month (${monthBirthdays.length})`} 
                />
              </Tabs>
            </Paper>
          )}

          {/* Department Breakdown */}
          {departmentBreakdown.length > 0 && activeTab === 0 && (
            <Paper sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: '#fafafa' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon fontSize="small" /> Department Breakdown (Today)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {departmentBreakdown.map((dept, idx) => (
                  <Chip
                    key={idx}
                    label={`${dept.department}: ${dept.count} staff`}
                    variant="outlined"
                    onClick={() => setDepartmentFilter(dept.department)}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#fff3e0' } }}
                  />
                ))}
              </Box>
            </Paper>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search by name, department, or phone..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              sx={{ flex: 2, minWidth: 200 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Department</InputLabel>
              <Select value={departmentFilter} onChange={(e) => { setDepartmentFilter(e.target.value); setPage(1); }} label="Department">
                <MenuItem value="all">All Departments</MenuItem>
                {uniqueDepartments.filter(d => d !== 'all').map((dept, idx) => (
                  <MenuItem key={idx} value={dept}>{dept}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Rows</InputLabel>
              <Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(e.target.value); setPage(1); }} label="Rows">
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportToCSV} disabled={filteredStaff.length === 0}>Export CSV</Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchBirthdayStaff} disabled={loading}>Refresh</Button>
          </Box>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={fetchBirthdayStaff}>Retry</Button>}>
              {error}
            </Alert>
          )}

          {!loading && !error && filteredStaff.length === 0 && activeTab === 0 && (
            <Alert severity="info" sx={{ mb: 2 }} icon={<CakeIcon />}>
              No staff members are celebrating their birthday today. Check the "This Month" tab for upcoming birthdays!
            </Alert>
          )}

          {!loading && !error && filteredStaff.length === 0 && activeTab === 1 && (
            <Alert severity="info" sx={{ mb: 2 }} icon={<CalendarMonthIcon />}>
              No staff members are celebrating their birthday this month.
            </Alert>
          )}

          {!loading && !error && filteredStaff.length > 0 && (
            <>
              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table size="medium">
                  <TableHead sx={{ bgcolor: activeTab === 0 ? '#fff3e0' : '#e3f2fd' }}>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Staff Member</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell align="center">Age</TableCell>
                      <TableCell align="center">Birthday</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedStaff.map((staff, index) => {
                      const globalIndex = (page - 1) * rowsPerPage + index + 1;
                      const isToday = activeTab === 0;
                      return (
                        <TableRow 
                          key={staff.id} 
                          hover 
                          sx={{ 
                            backgroundColor: isToday ? alpha('#ff9800', 0.08) : 'transparent',
                            '&:hover': { backgroundColor: isToday ? alpha('#ff9800', 0.15) : alpha('#000', 0.04) }
                          }}
                        >
                          <TableCell>{globalIndex}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Badge
                                overlap="circular"
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                badgeContent={isToday ? <TodayIcon sx={{ fontSize: 12, color: '#ff9800' }} /> : null}
                              >
                                <Avatar sx={{ bgcolor: isToday ? '#ff9800' : '#ffb74d', width: 48, height: 48 }}>
                                  <CakeIcon />
                                </Avatar>
                              </Badge>
                              <Box>
                                <Typography variant="body1" fontWeight="bold">
                                  {staff.full_name}
                                  {isToday && (
                                    <Chip 
                                      label="Today!" 
                                      size="small" 
                                      sx={{ ml: 1, bgcolor: '#ff9800', color: 'white', height: 20, fontSize: '0.7rem' }} 
                                    />
                                  )}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {staff.department}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={staff.department || 'Not Assigned'} size="small" variant="outlined" icon={<BusinessIcon />} />
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.5}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PhoneIcon sx={{ fontSize: 14, color: 'textSecondary' }} />
                                <Typography variant="body2">{staff.phone || 'N/A'}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <EmailIcon sx={{ fontSize: 14, color: 'textSecondary' }} />
                                <Typography variant="body2">{staff.email || 'N/A'}</Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={`Turning ${staff.age} years old!`}>
                              <Chip label={`${staff.age} years`} size="medium" sx={{
                                bgcolor: getAgeColor(staff.age) + '20',
                                color: getAgeColor(staff.age),
                                fontWeight: 'bold',
                                fontSize: '1rem',
                                minWidth: 80
                              }} />
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={staff.birthday_display} 
                              size="small" 
                              variant="outlined"
                              icon={<CakeIcon sx={{ fontSize: 14 }} />}
                              sx={{ fontWeight: 'medium' }}
                            />
                          </TableCell>
                          <TableCell align="center">
                           
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination count={totalPages} page={page} onChange={(e, value) => setPage(value)} color="primary" showFirstButton showLastButton />
                </Box>
              )}

              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
                Showing {paginatedStaff.length} of {filteredStaff.length} staff members
                {departmentFilter !== 'all' && ` in ${departmentFilter} department`}
                {searchTerm && ` matching "${searchTerm}"`}
              </Typography>
            </>
          )}

          {(todayBirthdays.length > 0 || monthBirthdays.length > 0) && (
            <Paper sx={{ p: 2, mt: 3, borderRadius: 2, bgcolor: '#fff8e1' }}>
             
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={onClose} variant="outlined">Close</Button>
          <Button onClick={() => { setSearchTerm(''); setDepartmentFilter('all'); setPage(1); }} variant="text">Reset Filters</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <SnackbarContent sx={{ bgcolor: snackbar.severity === 'success' ? '#4caf50' : '#d32f2f', color: 'white' }} message={snackbar.message} action={<IconButton size="small" onClick={handleCloseSnackbar} sx={{ color: 'white' }}><CloseIcon fontSize="small" /></IconButton>} />
      </Snackbar>
    </>
  );
};

export default BirthdayPopupDialog;